import { useEffect, useState } from 'react';
import { settings as Api, telegram as TgApi, type Settings as SettingsMap, type TelegramStatus } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Field, Row } from '../components/Field';
import { VideoStorage } from './VideoStorage';

/* Which setting belongs where, and what it is actually for. The API hands back
   a flat map of keys; grouping them is this screen's job. */
const GROUPS: { group: string; note?: string; keys: [key: string, label: string, hint?: string][] }[] = [
  {
    group: 'The business',
    note: 'This is the wording in the footer of every page and at the bottom of every email.',
    keys: [
      ['legal_name', 'Legal name'],
      ['proprietor', 'Proprietor'],
      ['gstin', 'GSTIN'],
      ['state_name', 'State'],
      ['sebi_reg', 'SEBI registration', 'The Research Analyst number, e.g. INH000027098. Left empty, nothing is claimed. Never put a number here that is not yours.'],
      ['bse_enlistment', 'BSE enlistment number', 'The RA enlistment with BSE. Shown beside the SEBI number.'],
      ['address', 'Registered address'],
    ],
  },
  {
    group: 'How people reach you',
    keys: [
      ['support_email', 'Support email'],
      ['whatsapp', 'WhatsApp number / link', 'Phone number (e.g. +91 95099 66984) or direct chat link.'],
      ['whatsapp_enabled', 'Show WhatsApp icon on Home', 'Select Enabled to show on Mobile App & Website, or Disabled to hide.'],
      ['telegram', 'Telegram link / handle', 'Channel link (e.g. https://t.me/+30DNSTSHXvoxYzU1) or @username.'],
      ['telegram_enabled', 'Show Telegram icon on Home', 'Select Enabled to show on Mobile App & Website, or Disabled to hide.'],
      ['alert_email', 'Where new enquiries are emailed'],
    ],
  },
  {
    group: 'Signing in',
    note: 'How many devices one member can be signed in on at once. Someone at the limit is asked to sign one out before a new one gets in.',
    keys: [
      ['device_limit_default', 'Devices per member', '0 means no limit. A member\'s own number, set on their page, overrides this.'],
    ],
  },
  {
    group: 'Wording',
    keys: [
      ['risk_disclaimer', 'Risk disclaimer'],
      ['email_footer', 'Email footer'],
    ],
  },
];

const SECRET_KEYS = ['payment_webhook_secret'];
const MULTILINE = ['address', 'risk_disclaimer', 'email_footer'];
const NUMERIC = ['device_limit_default'];
const BOOLEAN_KEYS = ['whatsapp_enabled', 'telegram_enabled'];

export function Settings() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [values, setValues] = useState<SettingsMap>({});
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (data) { setValues(data.settings); setDirty(false); } }, [data]);

  const set = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setDirty(true);
  };

  async function save() {
    setStatus('Saving…');
    try {
      await Api.save(values);
      setDirty(false);
      setStatus('Saved — live on the site now');
      await reload();
    } catch (e) { setStatus((e as Error).message); }
  }

  if (loading && !data) return <SkeletonPage />;
  if (error) return <div className="pg"><div className="err">Could not load settings — {error.message}</div></div>;

  const webhookUrl = data?.paymentWebhookUrl || 'https://app.tradevix.in/api/payments/webhook';
  const webhookOn = !!String(data?.settings.payment_webhook_secret ?? '').trim();

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* the field selects on focus, so a manual copy still works */ }
  }

  const placeholderWhatsapp = /x/i.test(values.whatsapp ?? '');

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Settings</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            The details every page and every email repeats.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: dirty ? 'var(--gold)' : 'var(--tx3)' }}>
            {dirty ? 'Unsaved changes' : status}
          </span>
          <button className="b bg" type="button" disabled={!dirty} onClick={() => void save()}>Save changes</button>
        </div>
      </div>

      {placeholderWhatsapp ? (
        <div className="crd" style={{ padding: '14px 18px', marginBottom: 16, borderColor: 'rgba(var(--goldrgb), .28)' }}>
          <p style={{ fontSize: 13, color: 'var(--tx2)', margin: 0 }}>
            The WhatsApp number is still the placeholder. The contact page shows it as plain text rather
            than a link, so nobody taps through to nothing — but it is also the number people will try to read.
          </p>
        </div>
      ) : null}

      {GROUPS.map((g) => (
        <div key={g.group} className="crd" style={{ padding: '20px 22px', marginBottom: 16 }}>
          <div className="kh" style={{ marginBottom: g.note ? 6 : 14 }}>{g.group}</div>
          {g.note ? <p className="hint" style={{ marginBottom: 14 }}>{g.note}</p> : null}
          <Row cols={2}>
            {g.keys.map(([key, label, hint]) => (
              <div key={key} style={MULTILINE.includes(key) ? { gridColumn: '1 / -1' } : undefined}>
                <Field label={label} hint={hint}>
                  {BOOLEAN_KEYS.includes(key)
                    ? <select className="inp" value={values[key] === '0' ? '0' : '1'}
                        onChange={(e) => set(key, e.target.value)}>
                        <option value="1">Enabled (Show icon)</option>
                        <option value="0">Disabled (Hide icon)</option>
                      </select>
                    : MULTILINE.includes(key)
                    ? <textarea className="inp ta" rows={2} value={values[key] ?? ''}
                        onChange={(e) => set(key, e.target.value)} />
                    : NUMERIC.includes(key)
                      ? <input className="inp" type="number" min={0} max={50} step={1} inputMode="numeric"
                          value={values[key] ?? ''} onChange={(e) => set(key, e.target.value)} />
                      : <input className="inp" value={values[key] ?? ''}
                          onChange={(e) => set(key, e.target.value)} />}
                </Field>
              </div>
            ))}
          </Row>
        </div>
      ))}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div className="kh">Payments</div>
          {/* The saved value decides, not what is typed: an unsaved secret
              has not opened anything yet. */}
          <span className={`tg ${webhookOn ? 't-up' : 't-nu'}`}>{webhookOn ? 'Webhook on' : 'Webhook off'}</span>
        </div>
        <Field label="Default payment link"
          hint="Where checkout sends a signed-in member to pay, when the plan or course has no link of its own. Must start with https://. Empty, and checkout asks people to contact you instead.">
          <input className="inp" placeholder="https://cosmofeed.com/…" value={values.payment_link ?? ''}
            onChange={(e) => set('payment_link', e.target.value)} />
        </Field>
        {/dummy|test|example/i.test(values.payment_link ?? '') ? (
          <p className="hint" style={{ color: 'var(--goldtx)', margin: '-4px 0 14px' }}>
            This looks like a placeholder link. Members pressing Pay will land on it — replace it with the real payment page before launch.
          </p>
        ) : null}
        <p className="hint" style={{ marginBottom: 14 }}>
          Give the payment gateway the URL below. It confirms a payment by calling it, and the member's plan
          is granted the moment it does. <strong>An empty secret keeps the endpoint closed.</strong>
        </p>
        <Field label="Webhook URL" hint="On app.tradevix.in, so it keeps working when the website moves to tradevix.in.">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="inp" readOnly value={webhookUrl} onFocus={(e) => e.currentTarget.select()}
              style={{ fontFamily: 'var(--mono, ui-monospace, monospace)' }} />
            <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
              onClick={() => void copyUrl()}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </Field>
        {SECRET_KEYS.map((key) => (
          <Field key={key} label="Webhook secret" hint="Use the secret the gateway gives you, or a long random one — never a password you use anywhere else.">
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="inp" type={showSecret ? 'text' : 'password'} value={values[key] ?? ''}
                autoComplete="off" onChange={(e) => set(key, e.target.value)} />
              <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
                onClick={() => setShowSecret((v) => !v)}>{showSecret ? 'Hide' : 'Show'}</button>
            </div>
          </Field>
        ))}
      </div>

      <TelegramCard values={values} set={set} reloadKey={data?.settings.tg_bot_token ?? ''} />

      <VideoStorage />

      {/* Screens that hold credentials and send real things stay where they
          are for now — porting them buys nothing and risks the mail path. */}
      <div className="crd" style={{ padding: '20px 22px', marginTop: 16 }}>
        <div className="kh" style={{ marginBottom: 10 }}>Still on the old console</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a className="b bo" href="/settings/email/">Email accounts ↗</a>
          <a className="b bo" href="/settings/mobile-otp/">Mobile &amp; OTP ↗</a>
          <a className="b bo" href="/settings/backups/">Backups ↗</a>
        </div>
      </div>
    </div>
  );
}

/* Trade calls from a Telegram channel. The token is saved with the rest of
   the page; saving a new one connects the bot, so there is nothing to press
   except Save. */
function TelegramCard({ values, set, reloadKey }: { values: SettingsMap; set: (k: string, v: string) => void; reloadKey: string }) {
  const [st, setSt] = useState<TelegramStatus | null>(null);
  const [msg, setMsg] = useState('');
  const [show, setShow] = useState(false);
  const load = () => { TgApi.status().then(setSt).catch(() => setSt(null)); };
  useEffect(load, [reloadKey]);

  async function connect() {
    setMsg('Connecting…');
    try { const r = await TgApi.connect(); setMsg(`Connected to @${r.bot}`); load(); }
    catch (e) { setMsg((e as Error).message); }
  }

  const seen = st?.seen;
  return (
    <div className="crd" style={{ padding: '20px 22px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div className="kh">Trade calls from Telegram</div>
        <span className={`tg ${st?.connected ? 't-up' : 't-nu'}`}>{st?.connected ? `Connected · @${st.bot}` : 'Not connected'}</span>
      </div>
      <p className="hint" style={{ marginBottom: 14 }}>
        Create a bot with @BotFather, add it to the channel as an admin, paste its token here and save.
        Posts that state an instrument, a target and a stop loss become trade calls; a reply such as
        "target achieved" or "SL hit" closes the call it answers.
      </p>
      <Row cols={2}>
        <Field label="Bot token" hint="From @BotFather. Treat it like a password.">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="inp" type={show ? 'text' : 'password'} autoComplete="off" value={values.tg_bot_token ?? ''}
              onChange={(e) => set('tg_bot_token', e.target.value)} />
            <button className="b bo" type="button" style={{ padding: '9px 14px', fontSize: 12 }}
              onClick={() => setShow((v) => !v)}>{show ? 'Hide' : 'Show'}</button>
          </div>
        </Field>
        <Field label="Channel or group id" hint="Only this chat is listened to. Post once in it and the id appears below.">
          <input className="inp" placeholder="-1001234567890" value={values.tg_chat_id ?? ''}
            onChange={(e) => set('tg_chat_id', e.target.value)} />
        </Field>
        <Field label="New calls arrive as" hint="Drafts wait for you to publish them. Live goes to members at once.">
          <select className="inp" value={values.tg_mode === 'publish' ? 'publish' : 'draft'}
            onChange={(e) => set('tg_mode', e.target.value)}>
            <option value="draft">Drafts (recommended)</option>
            <option value="publish">Live immediately</option>
          </select>
        </Field>
        <Field label="Allowed senders (groups only)" hint="Telegram user ids, comma separated. A channel's own posts need none.">
          <input className="inp" value={values.tg_senders ?? ''} onChange={(e) => set('tg_senders', e.target.value)} />
        </Field>
      </Row>
      {seen ? (
        <p className="hint" style={{ margin: '4px 0 10px' }}>
          Last heard from: <strong>{seen.title || 'a chat'}</strong> ({seen.id}){' '}
          {String(values.tg_chat_id ?? '').trim() !== seen.id ? (
            <button className="b bo" type="button" style={{ padding: '4px 10px', fontSize: 11.5 }}
              onClick={() => set('tg_chat_id', seen.id)}>Use this chat</button>
          ) : '— this is the connected chat.'}
        </p>
      ) : null}
      {st?.last_error ? <p className="hint" style={{ color: 'var(--down, #e5484d)' }}>Telegram says: {st.last_error}</p> : null}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="b bo" type="button" disabled={!st?.configured} onClick={() => void connect()}>Reconnect</button>
        <span className="hint">{msg || (st?.calls ? `${st.calls} calls made from Telegram so far` : '')}</span>
      </div>
    </div>
  );
}
